import Divider from '@mui/material/Divider';
import { i18n } from 'i18n';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Trans } from '@lingui/react';
import { throughputUnitLabelFromThroughputControlType } from 'hooks/useDisplayUnits/display';
import { BasicDisplayUnits, getPressurePumpUnitLabel, unitLabels } from 'hooks/useDisplayUnits/useDisplayUnits';
import { round } from 'utils';
import {
  BackfillRecipeDto,
  CustomUnitFields,
  MetricPressureUnit,
  ThroughputControlType,
  UnitSystem,
  UcsCoefficientsDto,
  PumpType
} from 'providers/api';
import { pumpPressureBarToGravityMetricHead, pumpPressureBarToKpa } from 'utils/unitConversions';
import ProjectContext from 'modules/project/providers/project/context';
import UnitSystemDetail, { UnitSystemValue } from './UnitSystemDetail';

interface BoosterPumpNode {
  nodeId: string;
  pressure: number;
}

interface RecipePasteFillDetailsProps {
  recipe: BackfillRecipeDto;
  unitSystemPreference: UnitSystem;
  throughputControlType: ThroughputControlType;
  targetDaysCoefficients: { [key: string]: UcsCoefficientsDto; }
  customUnitFields: CustomUnitFields[];
  pumpType: PumpType;
  boosterPumpNodes?: BoosterPumpNode[];
}

const RecipePasteFillDetails = ({
  recipe,
  unitSystemPreference,
  throughputControlType,
  targetDaysCoefficients,
  customUnitFields,
  pumpType,
  boosterPumpNodes,
}: RecipePasteFillDetailsProps) => {
  const projectContext = React.useContext(ProjectContext);
  const metricPressureUnit = projectContext?.state.metricPressureUnit ?? MetricPressureUnit.Bar;
  const isKiloPascal = metricPressureUnit === MetricPressureUnit.KiloPascal;
  const toDisplayPumpOrGravity = (value: number | undefined) => {
    if (value === undefined) {
      return value;
    }
    if (pumpType === PumpType.Pump) {
      return unitSystemPreference === UnitSystem.Metric && isKiloPascal ? pumpPressureBarToKpa(value) : value;
    }
    return pumpPressureBarToGravityMetricHead(value);
  };
  const pressureLabel = pumpType === PumpType.Pump ? 'Pump Pressure' : 'Gravity';
  const pressureUnitInfo = pumpType === PumpType.Pump ? getPressurePumpUnitLabel(metricPressureUnit) : unitLabels[BasicDisplayUnits.Gravity];

  const targetDays = recipe.pasteSpecification?.targetDays ?? (Object.keys(targetDaysCoefficients ?? {})[0] as unknown as number);
  const targetDaysLabel = `${targetDays} ${i18n._('days')}`;

  return (
    <>
      <UnitSystemDetail
        designed={recipe.pasteSpecification?.throughput}
        selected={recipe.pasteSpecification?.throughput}
        unitSystem={unitSystemPreference}
        unitInfo={throughputUnitLabelFromThroughputControlType(throughputControlType)}
        decimalPlaces={2}
        customUnitFields={customUnitFields}
      />

      <UnitSystemDetail
        designed={toDisplayPumpOrGravity(recipe.pasteSpecification?.pumpPressure)}
        selected={toDisplayPumpOrGravity(recipe.pasteSelectedSpecification?.pumpPressure)}
        unitSystem={unitSystemPreference}
        unitInfo={pressureUnitInfo}
        decimalPlaces={pumpType === PumpType.Pump && isKiloPascal ? 0 : 1}
        customUnitFields={customUnitFields}
      />

      <UnitSystemDetail
        designed={recipe.pasteSpecification?.targetUCSStrength}
        selected={recipe.pasteSelectedSpecification?.ucsStrength}
        unitSystem={unitSystemPreference}
        unitInfo={unitLabels[BasicDisplayUnits.PressureUCS]}
        decimalPlaces={2}
        customUnitFields={customUnitFields}
      />

      <TableRow>
        <TableCell component="th" scope="row"><Trans id="Days">Days</Trans></TableCell>
        <TableCell align="right" width="auto">{targetDaysLabel}</TableCell>
        <TableCell align="right">{targetDaysLabel}</TableCell>
      </TableRow>

      <TableRow>
        <TableCell component="th" scope="row"><Trans id="Concentration">Concentration</Trans></TableCell>
        <TableCell align="right" width="auto">{`${recipe.pasteOptimisedSpecification?.massConcentration} %m`}</TableCell>
        <TableCell align="right">{`${recipe.pasteSelectedSpecification?.massConcentration} %m`}</TableCell>
      </TableRow>

      <TableRow>
        <TableCell component="th" scope="row"><Trans id="Binder">Binder</Trans></TableCell>
        <TableCell align="right" width="auto">{`${recipe.pasteOptimisedSpecification?.binderContent} %`}</TableCell>
        <TableCell align="right">{`${recipe.pasteSelectedSpecification?.binderContent} %`}</TableCell>
      </TableRow>

      {boosterPumpNodes && boosterPumpNodes.length > 0 && (
        <>
          <TableRow>
            <TableCell colSpan={3} sx={{ p: 0 }}>
              <Divider sx={{ my: 1 }} />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell colSpan={3} sx={{ py: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                <Trans id="Booster Pump Pressure">Booster Pump Pressure</Trans>
              </Typography>
            </TableCell>
          </TableRow>
          {boosterPumpNodes.map((bp) => (
            <TableRow key={bp.nodeId}>
              <TableCell component="th" scope="row">{bp.nodeId}</TableCell>
              <TableCell align="right" colSpan={2}>
                <UnitSystemValue
                  value={toDisplayPumpOrGravity(bp.pressure)}
                  unitSystem={unitSystemPreference}
                  unitInfo={pressureUnitInfo}
                  decimalPlaces={1}
                  customUnitFields={customUnitFields}
                />
              </TableCell>
            </TableRow>
          ))}
        </>
      )}

      {(recipe.pasteSlackFlowSpecification || recipe.pasteMaxPressureSpecification) && (
        <>
          <TableRow>
            <TableCell colSpan={3} sx={{ p: 0 }}>
              <Divider sx={{ my: 1 }} />
            </TableCell>
          </TableRow>

          {recipe.pasteSlackFlowSpecification && (
            <>
              <TableRow>
                <TableCell colSpan={3} sx={{ py: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    <Trans id="Slack Flow">Slack Flow</Trans>
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row"><Trans id={pressureLabel}>{pressureLabel}</Trans></TableCell>
                <TableCell align="right" colSpan={2}>
                  <UnitSystemValue
                    value={toDisplayPumpOrGravity(recipe.pasteSlackFlowSpecification.pumpPressure)}
                    unitSystem={unitSystemPreference}
                    unitInfo={pressureUnitInfo}
                    decimalPlaces={1}
                    customUnitFields={customUnitFields}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row"><Trans id="Mass Conc.">Mass Conc.</Trans></TableCell>
                <TableCell align="right" colSpan={2}>
                  {`${round(recipe.pasteSlackFlowSpecification.massConcentration, 2)} %`}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row"><Trans id="Binder">Binder</Trans></TableCell>
                <TableCell align="right" colSpan={2}>
                  {`${round(recipe.pasteSlackFlowSpecification.binderContent, 2)} %`}
                </TableCell>
              </TableRow>
            </>
          )}

          {recipe.pasteMaxPressureSpecification && (
            <>
              <TableRow>
                <TableCell colSpan={3} sx={{ py: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    <Trans id="Max Pressure">Max Pressure</Trans>
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row"><Trans id={pressureLabel}>{pressureLabel}</Trans></TableCell>
                <TableCell align="right" colSpan={2}>
                  <UnitSystemValue
                    value={toDisplayPumpOrGravity(recipe.pasteMaxPressureSpecification.pumpPressure)}
                    unitSystem={unitSystemPreference}
                    unitInfo={pressureUnitInfo}
                    decimalPlaces={1}
                    customUnitFields={customUnitFields}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row"><Trans id="Mass Conc.">Mass Conc.</Trans></TableCell>
                <TableCell align="right" colSpan={2}>
                  {`${round(recipe.pasteMaxPressureSpecification.massConcentration, 2)} %`}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row"><Trans id="Binder">Binder</Trans></TableCell>
                <TableCell align="right" colSpan={2}>
                  {`${round(recipe.pasteMaxPressureSpecification.binderContent, 2)} %`}
                </TableCell>
              </TableRow>
            </>
          )}
        </>
      )}
    </>
  );
};
export default RecipePasteFillDetails;
